import type { RegisterParams } from '@common/workflow/serviceActivityRegistry';
import { CustomControlCode } from '@common/enums';
import { noteSections } from '@common/validation/customControls/note';
import { ServiceActivityRegistry } from '@common/workflow/serviceActivityRegistry';
import { addToMdoSections } from '../addToMdo';
import {
    bariatricsExternalReferralDraftSchema,
    bariatricsExternalReferralFinalSchema,
    bariatricsExternalReferralReadSchema,
} from '../bariatrics/bariatricsExternalReferral/v1/bariatricsExternalReferralSchema';
import {
    bariPreAddGeneralPractitionerDraftSchema,
    bariPreAddGeneralPractitionerFinalSchema,
    bariPreAddGeneralPractitionerReadSchema,
} from './bariPreAddGeneralPractitioner/v1/bariPreAddGeneralPractitionerSchemaV1';
import {
    bariPreCaroAccountNotActivatedNotificationDraftSchema,
    bariPreCaroAccountNotActivatedNotificationFinalSchema,
} from './bariPreCaroAccountNotActivatedNotification/v1/bariPreCaroAccountNotActivatedNotificationSchemaV1';
import {
    bariPreCaroInactivityPoliReportDraftSchema,
    bariPreCaroInactivityPoliReportFinalSchema,
    bariPreCaroInactivityPoliReportReadSchema,
} from './bariPreCaroInactivityPoliReport/v1/bariPreCaroInactivityPoliReportSchemaV1';
import {
    bariPreCloseProcessPrematurelyDraftSchema,
    bariPreCloseProcessPrematurelyFinalSchema,
    bariPreCloseProcessPrematurelyReadSchema,
} from './bariPreCloseProcessPrematurely/v1/bariPreCloseProcessPrematurelySchemaV1';
import {
    bariPreDietPlanAppointmentDraftSchema,
    bariPreDietPlanAppointmentFinalSchema,
    bariPreDietPlanAppointmentReadSchema,
} from './bariPreDietPlanAppointment/v1/bariPreDietPlanAppointmentSchemaV1';
import {
    bariPreDietScreeningDraftSchema,
    bariPreDietScreeningFinalSchema,
    bariPreDietScreeningReadSchema,
} from './bariPreDietScreening/v1/bariPreDietScreeningSchemaV1';
import {
    bariPreGroupSessionDraftSchema,
    bariPreGroupSessionFinalSchema,
    bariPreGroupSessionReadSchema,
} from './bariPreGroupSession/v1/bariPreGroupSessionSchemaV1';
import {
    bariPreMdoInterimReportDraftSchema,
    bariPreMdoInterimReportFinalSchema,
    bariPreMdoInterimReportReadSchema,
} from './bariPreMdoInterimReport/v1/bariPreMdoInterimReportSchemaV1';
import {
    bariPreMdoReportDraftSchema,
    bariPreMdoReportFinalSchema,
    bariPreMdoReportReadSchema,
} from './bariPreMdoReport/v1/bariPreMdoReportSchemaV1';
import {
    bariPreMedConsultAssessmentDraftSchema,
    bariPreMedConsultAssessmentFinalSchema,
    bariPreMedConsultAssessmentReadSchema,
} from './bariPreMedConsultAssessment/v1/bariPreMedConsultAssessmentSchemaV1';
import {
    bariPreMedConsultExamResultsDraftSchema,
    bariPreMedConsultExamResultsFinalSchema,
    bariPreMedConsultExamResultsReadSchema,
} from './bariPreMedConsultExamResults/v1/bariPreMedConsultExamResultsSchemaV1';
import {
    bariPreMedConsultPlanAppointmentDraftSchema,
    bariPreMedConsultPlanAppointmentFinalSchema,
    bariPreMedConsultPlanAppointmentReadSchema,
} from './bariPreMedConsultPlanAppointment/v1/bariPreMedConsultPlanAppointmentSchemaV1';
import {
    bariPreMedConsultReportFinalSchema,
    bariPreMedConsultReportReadSchema,
} from './bariPreMedConsultReport/v1/bariPreMedConsultReportSchemaV1';
import './bariPreNurseSpecialistMedScreeningReport/register';
import {
    bariPrePsychQuestionnaireDraftSchema,
    bariPrePsychQuestionnaireFinalSchema,
    bariPrePsychQuestionnaireReadSchema,
} from './bariPrePsychQuestionnaire/v1/bariPrePsychQuestionnaireSchemaV1';
import {
    bariPrePsychScreeningDraftSchema,
    bariPrePsychScreeningFinalSchema,
    bariPrePsychScreeningReadSchema,
} from './bariPrePsychScreening/v1/bariPrePsychScreeningSchemaV1';
import {
    bariPreReferralDraftSchema,
    bariPreReferralFinalSchema,
    bariPreReferralReadSchema,
} from './bariPreReferral/v1/bariPreReferralSchemaV1';
import {
    bariPreResumeMdoDraftSchema,
    bariPreResumeMdoFinalSchema,
    bariPreResumeMdoReadSchema,
} from './bariPreResumeMdo/v1/bariPreResumeMdoSchemaV1';
import {
    bariPreResumeMdoInterimDraftSchema,
    bariPreResumeMdoInterimFinalSchema,
    bariPreResumeMdoInterimReadSchema,
} from './bariPreResumeMdoInterim/v1/bariPreResumeMdoInterimSchemaV1';
import './bariPreSecretarialAssistantMedScreeningReport/register';

const registryElements: RegisterParams[] = [
    {
        customControlCode: CustomControlCode.BariPreAddGeneralPractitioner,
        draft: bariPreAddGeneralPractitionerDraftSchema,
        final: bariPreAddGeneralPractitionerFinalSchema,
        readSections: bariPreAddGeneralPractitionerReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreAddToMdo,
        sections: addToMdoSections,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreCaroAccountNotActivatedNotification,
        draft: bariPreCaroAccountNotActivatedNotificationDraftSchema,
        final: bariPreCaroAccountNotActivatedNotificationFinalSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreCaroInactivityPoliReport,
        draft: bariPreCaroInactivityPoliReportDraftSchema,
        final: bariPreCaroInactivityPoliReportFinalSchema,
        readSections: bariPreCaroInactivityPoliReportReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreCloseProcessPrematurely,
        draft: bariPreCloseProcessPrematurelyDraftSchema,
        final: bariPreCloseProcessPrematurelyFinalSchema,
        readSections: bariPreCloseProcessPrematurelyReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreDietPlanAppointment,
        draft: bariPreDietPlanAppointmentDraftSchema,
        final: bariPreDietPlanAppointmentFinalSchema,
        readSections: bariPreDietPlanAppointmentReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreDietScreening,
        draft: bariPreDietScreeningDraftSchema,
        final: bariPreDietScreeningFinalSchema,
        readSections: bariPreDietScreeningReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreGroupSession,
        draft: bariPreGroupSessionDraftSchema,
        final: bariPreGroupSessionFinalSchema,
        readSections: bariPreGroupSessionReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreMdoInterimReport,
        draft: bariPreMdoInterimReportDraftSchema,
        final: bariPreMdoInterimReportFinalSchema,
        readSections: bariPreMdoInterimReportReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreMdoReport,
        draft: bariPreMdoReportDraftSchema,
        final: bariPreMdoReportFinalSchema,
        readSections: bariPreMdoReportReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreMedConsultAssessment,
        draft: bariPreMedConsultAssessmentDraftSchema,
        final: bariPreMedConsultAssessmentFinalSchema,
        readSections: bariPreMedConsultAssessmentReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreMedConsultExamResults,
        draft: bariPreMedConsultExamResultsDraftSchema,
        final: bariPreMedConsultExamResultsFinalSchema,
        readSections: bariPreMedConsultExamResultsReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreMedConsultPlanAppointment,
        draft: bariPreMedConsultPlanAppointmentDraftSchema,
        final: bariPreMedConsultPlanAppointmentFinalSchema,
        readSections: bariPreMedConsultPlanAppointmentReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreMedConsultReport,
        draft: bariPreMedConsultReportFinalSchema,
        final: bariPreMedConsultReportFinalSchema,
        readSections: bariPreMedConsultReportReadSchema,
        version: 1,
    },

    {
        customControlCode: CustomControlCode.BariPreNote,
        sections: noteSections,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPrePsychQuestionnaire,
        draft: bariPrePsychQuestionnaireDraftSchema,
        final: bariPrePsychQuestionnaireFinalSchema,
        readSections: bariPrePsychQuestionnaireReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPrePsychScreening,
        draft: bariPrePsychScreeningDraftSchema,
        final: bariPrePsychScreeningFinalSchema,
        readSections: bariPrePsychScreeningReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreReferral,
        draft: bariPreReferralDraftSchema,
        final: bariPreReferralFinalSchema,
        readSections: bariPreReferralReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreExternalReferral,
        draft: bariatricsExternalReferralDraftSchema,
        final: bariatricsExternalReferralFinalSchema,
        readSections: bariatricsExternalReferralReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreResumeMdo,
        draft: bariPreResumeMdoDraftSchema,
        final: bariPreResumeMdoFinalSchema,
        readSections: bariPreResumeMdoReadSchema,
        version: 1,
    },
    {
        customControlCode: CustomControlCode.BariPreResumeMdoInterim,
        draft: bariPreResumeMdoInterimDraftSchema,
        final: bariPreResumeMdoInterimFinalSchema,
        readSections: bariPreResumeMdoInterimReadSchema,
        version: 1,
    },
];

ServiceActivityRegistry.instance.registerAll(registryElements);
